import { FC } from "react";
import type { NetworkMismatch } from "@/hooks/useNetworkCheck";

/// Name the network the wallet should be on, which is the app's own -- the
/// messages used to say "Preview" unconditionally, so the mainnet site told
/// anyone on a testnet wallet to switch to the network they were already on.
///
/// `wrong-testnet` is raised for any non-mainnet address when the app is
/// mainnet (see useNetworkCheck), so its wording cannot assume a testnet
/// either; both cases reduce to naming the target.
const targetNetwork = () =>
  window.__APP_CONFIG.env === "mainnet" ? "Mainnet" : "Preview";

const message = (mismatch: NonNullable<NetworkMismatch>): string => {
  const target = targetNetwork();
  // On a testnet app, a wrong *testnet* is worth distinguishing from a mainnet
  // wallet: the user is already on a testnet and the fix is a different one.
  if (mismatch === "wrong-testnet" && target !== "Mainnet") {
    return `Wrong testnet — switch wallet to ${target}`;
  }
  return `Wrong network — switch wallet to ${target}`;
};

interface NetworkMismatchBannerProps {
  mismatch: NonNullable<NetworkMismatch>;
}

export const NetworkMismatchBanner: FC<NetworkMismatchBannerProps> = ({ mismatch }) => {
  return (
    <div className="flex items-center gap-1.5 rounded-full border border-sr-tangerine/30 bg-sr-tangerine/10 px-3 py-1.5">
      <svg width="14" height="14" viewBox="0 0 20 20" fill="none" className="shrink-0 text-sr-tangerine">
        <path
          d="M10 6V10.5M10 13.5V14M3.5 17H16.5L10 3L3.5 17Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="text-xs font-medium text-sr-tangerine whitespace-nowrap">
        {message(mismatch)}
      </span>
    </div>
  );
};
